// src/components/user/UserViewTicket.jsx
// Role-aware — works for both users and auditors

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Container, Typography, Paper, Button, Grid,
  TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, IconButton,
  Chip, Alert, InputAdornment, Tooltip,
  Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Snackbar, Stack, Avatar, Collapse,
  Skeleton, Fade, Menu,
  AppBar, Toolbar, Slide, Card, CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import SearchIcon             from '@mui/icons-material/Search';
import ClearIcon              from '@mui/icons-material/Clear';
import FilterAltIcon          from '@mui/icons-material/FilterAlt';
import CloseIcon              from '@mui/icons-material/Close';
import ContentCopyIcon        from '@mui/icons-material/ContentCopy';
import AttachFileIcon         from '@mui/icons-material/AttachFile';
import DownloadIcon           from '@mui/icons-material/Download';
import MoreVertIcon           from '@mui/icons-material/MoreVert';
import VisibilityIcon         from '@mui/icons-material/Visibility';
import AddIcon                from '@mui/icons-material/Add';
import SupportIcon            from '@mui/icons-material/SupportAgent';
import ReportProblemIcon      from '@mui/icons-material/ReportProblem';
import LocationOnIcon         from '@mui/icons-material/LocationOn';
import InventoryIcon          from '@mui/icons-material/Inventory';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AssignmentIcon         from '@mui/icons-material/Assignment';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import PriorityHighIcon       from '@mui/icons-material/PriorityHigh';

import { format }               from 'date-fns';
import { useAuth }              from '../../contexts/AuthContext';
import { helpDeskService, ROLES } from '../../services/helpDeskService';
import { singleValueListService } from '../../services/singleValueListService';
import NewTicketForm            from './NewTicketForm';

// ─── Constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 30_000;

// ─── Slide Transition ─────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─── Role-Specific UI Configuration ──────────────────────────────────────────

const ROLE_UI = {
  [ROLES.USER]: {
    headerTitle:    'My Support Tickets',
    headerSubtitle: 'View, track and manage your support requests',
    newButtonLabel: 'New Ticket',
    newButtonIcon:  <AddIcon />,
    accentColor:    '#2563eb',
    emptyIcon:      <SupportIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />,
    emptyTitle:     'No tickets found',
    emptyEmpty:     'You have not created any support tickets yet',
  },
  [ROLES.AUDITOR]: {
    headerTitle:    'Audit Support Tickets',
    headerSubtitle: 'Raise and track issues found during your audits',
    newButtonLabel: 'Raise Issue',
    newButtonIcon:  <ReportProblemIcon />,
    accentColor:    '#dc2626',
    emptyIcon:      <ReportProblemIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />,
    emptyTitle:     'No tickets raised yet',
    emptyEmpty:     'Raise tickets for missing or damaged assets during your audits',
  },
};

// ─── Pure UI Helpers ──────────────────────────────────────────────────────────

const getColorForPriority = (priority) => {
  const map = {
    Critical: { bg: '#ffebee', text: '#c62828' },
    High:     { bg: '#fff3e0', text: '#ef6c00' },
    Medium:   { bg: '#e3f2fd', text: '#1565c0' },
    Low:      { bg: '#e8f5e9', text: '#2e7d32' },
  };
  return map[priority] || { bg: '#ececec', text: '#595959' };
};

const getColorForStatus = (status) => {
  const map = {
    Open:          { bg: '#e3f2fd', text: '#1565c0' },
    'In Progress': { bg: '#fff9c4', text: '#9a8900' },
    Resolved:      { bg: '#e8f5e9', text: '#2e7d32' },
    Closed:        { bg: '#ececec', text: '#666'    },
  };
  return map[status] || { bg: '#ececec', text: '#595959' };
};

const formatCurrency = (amount) => {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  }).format(amount);
};

const safeDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  const d = safeDate(value);
  if (!d) return 'N/A';
  try { return format(d, 'MMM dd, yyyy hh:mm a'); }
  catch { return 'N/A'; }
};

// FIX: createdAt lives in metadata, never top-level. Safe accessor for display.
const getCreatedAt = (ticket) => ticket?.metadata?.createdAt ?? null;

// ─── Styled Components ────────────────────────────────────────────────────────

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: '1px solid #e2e8f0',
}));

const SectionTitle = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.9375rem',
  fontWeight: 700,
  color: '#1e293b',
  marginBottom: '1.25rem',
  paddingBottom: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
}));

const DetailRow = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  marginBottom: '1rem',
  '&:last-child': { marginBottom: 0 },
}));

const DetailLabel = styled(Typography)(() => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}));

const DetailValue = styled(Typography)(() => ({
  fontSize: '0.9375rem',
  color: '#111827',
  lineHeight: 1.5,
}));

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const TicketsSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Skeleton variant="text" width={280} height={44} />
        <Skeleton variant="text" width={400} height={20} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Skeleton variant="rounded" width={130} height={40} />
        <Skeleton variant="rounded" width={110} height={40} />
      </Box>
    </Box>
    <Skeleton variant="rounded" height={70} sx={{ borderRadius: 3 }} />
    <Skeleton variant="rounded" height={400} sx={{ borderRadius: 3 }} />
  </Container>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const UserViewTicket = ({ role: propRole }) => {
  const { currentUser } = useAuth();

  const role = useMemo(() => {
    const candidate = propRole || currentUser?.role || ROLES.USER;
    return Object.values(ROLES).includes(candidate?.toLowerCase())
      ? candidate.toLowerCase()
      : ROLES.USER;
  }, [propRole, currentUser?.role]);

  const uiConfig = ROLE_UI[role] || ROLE_UI[ROLES.USER];

  // ── Data state ──────────────────────────────────────────────────────────
  const [tickets,        setTickets]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // ── Dialog state ────────────────────────────────────────────────────────
  const [selectedTicket,      setSelectedTicket]      = useState(null);
  const [dialogOpen,          setDialogOpen]          = useState(false);
  const [newTicketDialogOpen, setNewTicketDialogOpen] = useState(false);

  // ── Context menu state ──────────────────────────────────────────────────
  const [anchorEl,   setAnchorEl]   = useState(null);
  const [menuTarget, setMenuTarget] = useState(null);

  const [statusOptions,   setStatusOptions]   = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);

  // ── Filter state ────────────────────────────────────────────────────────
  const [filters,     setFilters]     = useState({ status: 'All', priority: 'All', search: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [copied,      setCopied]      = useState(false);

  // ── Pagination ──────────────────────────────────────────────────────────
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ── Scroll ref for full-screen dialog body ──────────────────────────────
  const dialogBodyRef = useRef(null);

  const pollRef = useRef(null);

  // ── Notification helper ─────────────────────────────────────────────────
  const showNotification = useCallback((message, type = 'success') => {
    if (type === 'success') {
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 5000);
    } else {
      setError(message);
      setTimeout(() => setError(null), 6000);
    }
  }, []);

  // ── Fetch ───────────────────────────────────────────────────────────────
  const fetchTickets = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);

    // Safely resolve the user ID (falls back to localStorage if Context is slow)
    const storedUser = typeof window !== "undefined" ? JSON.parse(localStorage.getItem("userData") || "{}") : {};
    const userId = currentUser?.uid || currentUser?.id || currentUser?.userId || storedUser?.uid || storedUser?.id;
    const companyId = currentUser?.companyId || storedUser?.companyId;

    if (!userId || !companyId) {
      if (!silent) {
        setLoading(false);
        setError("User session incomplete. Please refresh the page or log in again.");
      }
      return;
    }

    try {
           const data = await helpDeskService.getTicketsForRole(role, {
        companyId: companyId,
        userId:    userId,
      
        ...(role === ROLES.AUDITOR && { raisedBy: ROLES.AUDITOR }),
      });
      setTickets(data);
    } catch (err) {
      console.error('[UserViewTicket] fetchTickets:', err);
      setError('Failed to load tickets. Please try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentUser, role]);

  useEffect(() => {
    fetchTickets();
    pollRef.current = setInterval(() => fetchTickets(true), POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchTickets]);

  // Load SVL dropdown options (statuses + priorities)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const storedUser =
        typeof window !== 'undefined'
          ? JSON.parse(localStorage.getItem('userData') || '{}')
          : {};
      const companyId = currentUser?.companyId || storedUser?.companyId;

      try {
        const [statuses, priorities] = await Promise.all([
          singleValueListService.getTicketStatuses({ companyId }),
          singleValueListService.getTicketPriorities({ companyId }),
        ]);
        if (cancelled) return;
        setStatusOptions(
          statuses.map(i => String(i.value ?? i.label ?? '')).filter(Boolean),
        );
        setPriorityOptions(
          priorities.map(i => String(i.value ?? i.label ?? '')).filter(Boolean),
        );
      } catch (err) {
        console.error('[UserViewTicket] SVL load:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.companyId]);

  // ── Scroll lock for full-screen dialog ──
  useEffect(() => {
    if (!dialogOpen) return undefined;
    const html = document.documentElement;
    const prevOverflow     = html.style.overflow;
    const prevPaddingRight = html.style.paddingRight;
    const scrollbarWidth   = window.innerWidth - document.documentElement.clientWidth;
    html.style.overflow = 'hidden';
    if (scrollbarWidth > 0) html.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      html.style.overflow     = prevOverflow;
      html.style.paddingRight = prevPaddingRight;
    };
  }, [dialogOpen]);

  // ── Filter pipeline ─────────────────────────────────────────────────────
  const filteredTickets = useMemo(() => {
    let result = [...tickets];
    if (filters.status !== 'All')   result = result.filter(t => t.status === filters.status);
    if (filters.priority !== 'All') result = result.filter(t => t.priority === filters.priority);
    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(t =>
        t.issueType?.toLowerCase().includes(term) ||
        t.description?.toLowerCase().includes(term) ||
        String(t.id).toLowerCase().includes(term) ||
        t.assetId?.toLowerCase().includes(term) ||
        t.assetName?.toLowerCase().includes(term),
      );
    }
    // FIX: sort by metadata.createdAt (top-level createdAt never exists).
    result.sort((a, b) => {
      const aMs = safeDate(getCreatedAt(a))?.getTime() || 0;
      const bMs = safeDate(getCreatedAt(b))?.getTime() || 0;
      return bMs - aMs;
    });
    return result;
  }, [tickets, filters]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleFilterChange = (name, value) =>
    setFilters(prev => ({ ...prev, [name]: value }));

  const resetFilters = () =>
    setFilters({ status: 'All', priority: 'All', search: '' });

  const handleCopyTicketLink = (ticketId) => {
    navigator.clipboard.writeText(
      `${window.location.origin}/${role}/tickets?id=${ticketId}`,
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const handleChangePage        = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen  = (e, ticket) => { setAnchorEl(e.currentTarget); setMenuTarget(ticket); };
  const handleMenuClose = ()          => { setAnchorEl(null); setMenuTarget(null); };

  const openTicketDialog = useCallback((ticket) => {
    setSelectedTicket(ticket);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTicket(null);
  };

  const handleViewInvoice = useCallback((url) => {
    try { helpDeskService.openInvoiceFile(url); }
    catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  // ── Derived: accent colour for the selected ticket's role ───────────────
  const accentColor      = uiConfig.accentColor;
  const accentColorText  = role === ROLES.AUDITOR ? '#dc2626' : '#1976d2';

  if (loading) return <TicketsSkeleton />;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* ── HEADER ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1"
            sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.5px' }}>
            {uiConfig.headerTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {uiConfig.headerSubtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={uiConfig.newButtonIcon}
            onClick={() => setNewTicketDialogOpen(true)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: accentColor,
              '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' },
            }}
          >
            {uiConfig.newButtonLabel}
          </Button>
          <Button variant="outlined" startIcon={<FilterAltIcon />}
            onClick={() => setShowFilters(v => !v)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            {showFilters ? 'Hide Filters' : 'Filters'}
          </Button>
        </Box>
      </Box>

      {/* ── SNACKBARS ── */}
      {copied && (
        <Snackbar open autoHideDuration={600} onClose={() => setCopied(false)}>
          <Alert severity="info" sx={{ fontWeight: 600 }}>Ticket link copied!</Alert>
        </Snackbar>
      )}
      <Snackbar open={!!successMessage} autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {typeof error === 'string' ? error : error?.message || 'An error occurred'}
        </Alert>
      </Snackbar>

      {/* ── SEARCH BAR ── */}
      <Paper sx={{ p: 2, borderRadius: 3, bgcolor: 'background.paper' }} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" variant="outlined"
              label="Search Tickets"
              placeholder="Search by type, description, ID..."
              value={filters.search}
              onChange={e => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'action.active' }} fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: filters.search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleFilterChange('search', '')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          {filters.search && (
            <Grid item xs={12} md={8}>
              <Typography variant="body2" color="text.secondary">
                Found <strong>{filteredTickets.length}</strong>{' '}
                ticket{filteredTickets.length !== 1 ? 's' : ''} matching "{filters.search}"
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* ── COLLAPSIBLE FILTER PANEL ── */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: 'background.paper' }} variant="outlined">
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} label="Status"
                  onChange={e => handleFilterChange('status', e.target.value)}
                  sx={{ borderRadius: 2 }}>
                  <MenuItem value="All">All Statuses</MenuItem>
                  {statusOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select value={filters.priority} label="Priority"
                  onChange={e => handleFilterChange('priority', e.target.value)}
                  sx={{ borderRadius: 2 }}>
                  <MenuItem value="All">All Priorities</MenuItem>
                  {priorityOptions.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}
              sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Button variant="text" color="info" onClick={resetFilters}
                startIcon={<ClearIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                Clear Filters
              </Button>
              <Tooltip title="Close filters" arrow>
                <IconButton size="small" onClick={() => setShowFilters(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* ── TABLE ── */}
      <TableContainer component={Paper} variant="outlined" sx={{
        borderRadius: 3,
        boxShadow: '0px 4px 16px rgba(0,0,0,0.02)',
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: 8 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.12)', borderRadius: 4 },
      }}>

        {filteredTickets.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            {uiConfig.emptyIcon}
            <Typography variant="h6" gutterBottom>{uiConfig.emptyTitle}</Typography>
            <Alert severity="info" sx={{ justifyContent: 'center', borderRadius: 2.5, mb: 2 }}>
              {tickets.length === 0 ? uiConfig.emptyEmpty : 'No tickets match your current filters'}
            </Alert>
            {tickets.length > 0 && (
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button onClick={resetFilters} sx={{ textTransform: 'none' }}>
                  Clear Filters
                </Button>
              </Stack>
            )}
          </Box>
        ) : (
          <Table sx={{ minWidth: 900 }} size="medium">
            <TableHead>
              <TableRow>
                {[
                  { label: 'Sl.No',       align: 'center' },
                  { label: 'Ticket ID',   align: 'left'   },
                  { label: 'Issue Type',  align: 'left'   },
                  { label: 'Priority',    align: 'left'   },
                  { label: 'Status',      align: 'left'   },
                  { label: 'Created',     align: 'left'   },
                  { label: 'Description', align: 'left'   },
                  { label: 'Asset',       align: 'left'   },
                  ...(role === ROLES.AUDITOR
                    ? [{ label: 'Location', align: 'left' }]
                    : []),
                  { label: 'Actions',     align: 'center' },
                ].map(({ label, align }) => (
                  <TableCell key={label} align={align} sx={{
                    fontWeight: 700,
                    bgcolor: 'background.neutral',
                    color: 'text.primary',
                    whiteSpace: 'nowrap',
                  }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredTickets
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((ticket, index) => {
                  const priorityColor = getColorForPriority(ticket.priority || 'Medium');
                  const statusColor   = getColorForStatus(ticket.status || 'Open');

                  return (
                    <TableRow key={ticket.id} hover>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          {page * rowsPerPage + index + 1}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            onClick={() => openTicketDialog(ticket)}
                            sx={{
                              fontFamily: 'monospace', fontWeight: 600,
                              color: 'primary.main', fontSize: '0.85rem',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}>
                            #{String(ticket.id).substring(0, 10)}
                          </Typography>
                          <Tooltip title="Copy Ticket Link" arrow>
                            <IconButton size="small"
                              onClick={e => { e.stopPropagation(); handleCopyTicketLink(ticket.id); }}>
                              <ContentCopyIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip variant="outlined" size="small"
                          label={String(ticket.issueType || 'General')}
                          sx={{
                            fontWeight: 500, fontSize: '0.72rem',
                            bgcolor:     role === ROLES.AUDITOR ? '#fef2f2' : '#fdf6e4',
                            color:       role === ROLES.AUDITOR ? '#991b1b' : '#bc7800',
                            borderColor: role === ROLES.AUDITOR ? '#fecaca' : '#edd286',
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip size="small"
                          label={String(ticket.priority || 'Medium')}
                          sx={{
                            background: priorityColor.bg, color: priorityColor.text,
                            fontWeight: 700, fontSize: '0.72rem',
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip size="small"
                          label={String(ticket.status || 'Open')}
                          sx={{
                            background: statusColor.bg, color: statusColor.text,
                            fontWeight: 700, fontSize: '0.72rem',
                          }}
                        />
                      </TableCell>

                      {/* FIX: read createdAt from metadata, format it, fall back to N/A */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(getCreatedAt(ticket))}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ maxWidth: 220 }}>
                        <Tooltip
                          title={String(ticket.description || 'No description')} arrow
                          disableHoverListener={!ticket.description || ticket.description.length <= 45}>
                          <Typography variant="body2" color="text.secondary"
                            onClick={() => openTicketDialog(ticket)}
                            sx={{
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap', maxWidth: 200, cursor: 'pointer',
                            }}>
                            {String(ticket.description || 'No description')}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        {ticket.assetId ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600}
                              sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {ticket.assetId}
                            </Typography>
                            {ticket.assetName && (
                              <Typography variant="caption" color="text.secondary">
                                {ticket.assetName}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                                         {role === ROLES.AUDITOR && (
                        <TableCell>
                          {ticket.locationCode ? (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {String(ticket.locationCode)}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <IconButton size="small" onClick={e => handleMenuOpen(e, ticket)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* ── PAGINATION ── */}
      {filteredTickets.length > 0 && (
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0,
        }}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Showing {page * rowsPerPage + 1} to{' '}
            {Math.min((page + 1) * rowsPerPage, filteredTickets.length)} of{' '}
            {filteredTickets.length} tickets
          </Typography>
          <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div"
            count={filteredTickets.length} rowsPerPage={rowsPerPage} page={page}
            onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ border: 'none', overflow: 'hidden' }}
          />
        </Box>
      )}

      {/* ── CONTEXT MENU ── */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
        TransitionComponent={Fade}
        PaperProps={{ sx: { minWidth: 200, borderRadius: 2.5, boxShadow: '0px 4px 20px rgba(0,0,0,0.08)' } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>

        <MenuItem onClick={() => { openTicketDialog(menuTarget); handleMenuClose(); }}
          sx={{ gap: 1.5, py: 1 }}>
          <VisibilityIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={500}>View Details</Typography>
        </MenuItem>

        <MenuItem onClick={() => { handleCopyTicketLink(menuTarget?.id); handleMenuClose(); }}
          sx={{ gap: 1.5, py: 1 }}>
          <ContentCopyIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={500}>Copy Ticket Link</Typography>
        </MenuItem>

        {/* FIX: maintenanceInvoice is the allowed key holding {fileName, downloadURL}. */}
        {menuTarget?.maintenanceInvoice?.downloadURL && (
          <MenuItem onClick={() => {
            handleViewInvoice(menuTarget.maintenanceInvoice.downloadURL);
            handleMenuClose();
          }} sx={{ gap: 1.5, py: 1 }}>
            <AttachFileIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>View Invoice</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* ═══════════════════════════════════════════════════════════════════
          FULL-SCREEN TICKET DETAIL DIALOG
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog
        fullScreen
        open={dialogOpen}
        onClose={handleCloseDialog}
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        {/* ── APP BAR ── */}
        <AppBar sx={{ position: 'relative', flexShrink: 0 }} elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseDialog}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>

            <Box sx={{ ml: 2, flex: 1 }}>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                Ticket #{String(selectedTicket?.id || '').substring(0, 10)}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {String(selectedTicket?.email || currentUser?.email || '')}
                {selectedTicket?.issueType ? ` · ${selectedTicket.issueType}` : ''}
              </Typography>
            </Box>

            {selectedTicket && (
              <Stack direction="row" spacing={1} sx={{ mr: 1 }}>
                <Chip
                  size="small"
                  label={String(selectedTicket.status || 'Open')}
                  sx={{
                    bgcolor: getColorForStatus(selectedTicket.status).bg,
                    color:   getColorForStatus(selectedTicket.status).text,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    border: 'none',
                  }}
                />
                <Chip
                  size="small"
                  label={String(selectedTicket.priority || 'Medium')}
                  sx={{
                    bgcolor: getColorForPriority(selectedTicket.priority).bg,
                    color:   getColorForPriority(selectedTicket.priority).text,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    border: 'none',
                  }}
                />
              </Stack>
            )}
          </Toolbar>
        </AppBar>

        {/* ── FLEX SHELL ── */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#f1f5f9',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* ── SCROLLABLE BODY ── */}
          <Box
            ref={dialogBodyRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              py: 4,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.15)', borderRadius: 3 },
            }}
          >
            {selectedTicket && (
              <Container maxWidth="lg">
                <Stack spacing={3}>

                  {/* ── SECTION 1: TICKET OVERVIEW ── */}
                  <SectionCard variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        <ConfirmationNumberIcon color="primary" fontSize="small" />
                        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b' }}>
                          Ticket Overview
                        </Typography>
                      </SectionTitle>

                      <Grid container spacing={3}>
                        {/* Ticket ID */}
                        <Grid item xs={12} sm={6} md={4}>
                          <DetailRow>
                            <DetailLabel>Ticket ID</DetailLabel>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography
                                sx={{
                                  fontFamily: 'monospace', fontWeight: 700,
                                  color: 'primary.main', fontSize: '0.95rem',
                                }}>
                                #{String(selectedTicket.id || '').substring(0, 10)}
                              </Typography>
                              <Tooltip title="Copy Ticket Link" arrow>
                                <IconButton size="small"
                                  onClick={() => handleCopyTicketLink(selectedTicket.id)}>
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </DetailRow>
                        </Grid>

                        {/* Status */}
                        <Grid item xs={12} sm={6} md={4}>
                          <DetailRow>
                            <DetailLabel>Status</DetailLabel>
                            <Box>
                              <Chip
                                label={String(selectedTicket.status || 'Open')}
                                sx={{
                                  bgcolor: getColorForStatus(selectedTicket.status).bg,
                                  color:   getColorForStatus(selectedTicket.status).text,
                                  fontWeight: 700, fontSize: '0.8rem', px: 0.5,
                                }}
                              />
                            </Box>
                          </DetailRow>
                        </Grid>

                        {/* Priority */}
                        <Grid item xs={12} sm={6} md={4}>
                          <DetailRow>
                            <DetailLabel>Priority</DetailLabel>
                            <Box>
                              <Chip
                                icon={<PriorityHighIcon sx={{ fontSize: '14px !important' }} />}
                                label={String(selectedTicket.priority || 'Medium')}
                                sx={{
                                  bgcolor: getColorForPriority(selectedTicket.priority).bg,
                                  color:   getColorForPriority(selectedTicket.priority).text,
                                  fontWeight: 700, fontSize: '0.8rem', px: 0.5,
                                  '& .MuiChip-icon': {
                                    color: getColorForPriority(selectedTicket.priority).text,
                                  },
                                }}
                              />
                            </Box>
                          </DetailRow>
                        </Grid>

                        {/* Issue Type */}
                        <Grid item xs={12} sm={6} md={4}>
                          <DetailRow>
                            <DetailLabel>Issue Type</DetailLabel>
                            <Chip
                              variant="outlined"
                              size="small"
                              label={String(selectedTicket.issueType || 'General')}
                              sx={{
                                width: 'fit-content',
                                fontWeight: 600, fontSize: '0.8rem',
                                bgcolor:     role === ROLES.AUDITOR ? '#fef2f2' : '#fdf6e4',
                                color:       role === ROLES.AUDITOR ? '#991b1b' : '#bc7800',
                                borderColor: role === ROLES.AUDITOR ? '#fecaca' : '#edd286',
                              }}
                            />
                          </DetailRow>
                        </Grid>

                        {/* Created — FIX: metadata.createdAt */}
                        <Grid item xs={12} sm={6} md={4}>
                          <DetailRow>
                            <DetailLabel>Created</DetailLabel>
                            <DetailValue>{formatDate(getCreatedAt(selectedTicket))}</DetailValue>
                          </DetailRow>
                        </Grid>

                        {/* Resolved At */}
                        {selectedTicket.resolvedAt && (
                          <Grid item xs={12} sm={6} md={4}>
                            <DetailRow>
                              <DetailLabel>Resolved At</DetailLabel>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                                <DetailValue sx={{ color: '#16a34a' }}>
                                  {formatDate(selectedTicket.resolvedAt)}
                                </DetailValue>
                              </Stack>
                            </DetailRow>
                          </Grid>
                        )}
                      </Grid>

                      {/* Description */}
                      <Box sx={{ mt: 2.5 }}>
                        <DetailLabel sx={{ mb: 0.75 }}>Description</DetailLabel>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2, borderRadius: 2, bgcolor: '#f9fafb',
                            borderColor: '#e5e7eb',
                          }}
                        >
                          <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.9375rem', color: '#374151' }}>
                            {String(selectedTicket.description || 'No description provided')}
                          </Typography>
                        </Paper>
                      </Box>
                    </CardContent>
                  </SectionCard>

                  {/* ── SECTION 2: ASSET & LOCATION ── */}
                                   {(selectedTicket.assetId ||
                    selectedTicket.locationCode ||
                    selectedTicket.auditEventTitle) && (
                    <SectionCard variant="outlined">
                      <CardContent sx={{ p: 3 }}>
                        <SectionTitle>
                          <InventoryIcon color="primary" fontSize="small" />
                          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b' }}>
                            Asset & Location
                          </Typography>
                        </SectionTitle>

                        <Grid container spacing={3}>
                          {selectedTicket.assetId && (
                            <Grid item xs={12} sm={6}>
                              <DetailRow>
                                <DetailLabel>Asset ID</DetailLabel>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <InventoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                  <Box>
                                    <Typography
                                      sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.9rem' }}>
                                      {selectedTicket.assetId}
                                    </Typography>
                                    {selectedTicket.assetName && (
                                      <Typography variant="caption" color="text.secondary">
                                        {selectedTicket.assetName}
                                      </Typography>
                                    )}
                                  </Box>
                                </Stack>
                              </DetailRow>
                            </Grid>
                          )}

                                                  {role === ROLES.AUDITOR && selectedTicket.locationCode && (
                            <Grid item xs={12} sm={6}>
                              <DetailRow>
                                <DetailLabel>Location</DetailLabel>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                  <DetailValue>{String(selectedTicket.locationCode)}</DetailValue>
                                </Stack>
                              </DetailRow>
                            </Grid>
                          )}

                          {role === ROLES.AUDITOR && selectedTicket.auditEventTitle && (
                            <Grid item xs={12}>
                              <DetailRow>
                                <DetailLabel>Related Audit</DetailLabel>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <AssignmentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                  <DetailValue>{selectedTicket.auditEventTitle}</DetailValue>
                                </Stack>
                              </DetailRow>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </SectionCard>
                  )}

                  {/* ── SECTION 3: RESOLUTION ── */}
                  {(selectedTicket.resolutionNotes ||
                    selectedTicket.resolutionCost != null ||
                    selectedTicket.maintenanceInvoice) && (
                    <SectionCard variant="outlined">
                      <CardContent sx={{ p: 3 }}>
                        <SectionTitle>
                          <CheckCircleIcon color="success" fontSize="small" />
                          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b' }}>
                            Resolution Details
                          </Typography>
                        </SectionTitle>

                        <Stack spacing={2.5}>
                          {selectedTicket.resolutionNotes && (
                            <DetailRow>
                              <DetailLabel>Resolution Notes</DetailLabel>
                              <Paper
                                variant="outlined"
                                sx={{ p: 2, borderRadius: 2, bgcolor: '#f0fdf4', borderColor: '#bbf7d0' }}
                              >
                                <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.9375rem', color: '#166534' }}>
                                  {String(selectedTicket.resolutionNotes)}
                                </Typography>
                              </Paper>
                            </DetailRow>
                          )}

                          {selectedTicket.resolutionCost != null && (
                            <DetailRow>
                              <DetailLabel>Resolution Cost</DetailLabel>
                              <Typography sx={{
                                bgcolor: '#f0fdf4', border: '1px solid #bbf7d0',
                                p: 1.5, borderRadius: 2,
                                fontWeight: 700, fontSize: '1rem', color: '#166534',
                                width: 'fit-content',
                              }}>
                                {formatCurrency(selectedTicket.resolutionCost)}
                              </Typography>
                            </DetailRow>
                          )}

                          {selectedTicket.maintenanceInvoice && (
                            <DetailRow>
                              <DetailLabel>Maintenance Invoice</DetailLabel>
                              <Paper
                                variant="outlined"
                                sx={{ p: 2, borderRadius: 2, bgcolor: '#eff6ff', borderColor: '#bfdbfe' }}
                              >
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                  <AttachFileIcon fontSize="small" color="info" />
                                  <Typography sx={{ flexGrow: 1, fontWeight: 500 }}>
                                    {selectedTicket.maintenanceInvoice.fileName}
                                  </Typography>
                                  <Tooltip title="Download Invoice" arrow>
                                    <IconButton
                                      size="small"
                                      color="info"
                                      onClick={() => handleViewInvoice(
                                        selectedTicket.maintenanceInvoice.downloadURL,
                                      )}
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </Paper>
                            </DetailRow>
                          )}
                        </Stack>
                      </CardContent>
                    </SectionCard>
                  )}

                  {/* ── SECTION 4 (COMMENTS) REMOVED ──
                      `comments` is not an ALLOWED_KEY on HelpDeskTicketModel,
                      so it never persists. Display and input removed entirely. */}

                </Stack>
              </Container>
            )}
          </Box>

          {/* ── STICKY FOOTER ── */}
          <Box
            sx={{
              borderTop: '1px solid #e2e8f0',
              backgroundColor: '#fff',
              py: 2,
              px: 3,
              boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
              flexShrink: 0,
            }}
          >
            <Container maxWidth={false} disableGutters>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => handleCopyTicketLink(selectedTicket?.id)}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 500,
                    color: '#374151',
                    borderColor: '#d1d5db',
                    backgroundColor: '#fff',
                    '&:hover': { backgroundColor: '#f9fafb', borderColor: '#9ca3af' },
                  }}
                >
                  Copy Link
                </Button>

                <Button
                  onClick={handleCloseDialog}
                  sx={{
                    px: 3,
                    py: 1,
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#e5e7eb' },
                  }}
                >
                  Close
                </Button>
              </Box>
            </Container>
          </Box>
        </Box>
      </Dialog>

      {/* ── NEW TICKET DIALOG ── */}
      <NewTicketForm
        open={newTicketDialogOpen}
        onClose={() => setNewTicketDialogOpen(false)}
        onSuccess={(msg) => {
          showNotification(msg);
          setNewTicketDialogOpen(false);
          fetchTickets(true);
        }}
        role={role}
      />
    </Container>
  );
};

export default UserViewTicket;